<template>
  <div class="br-wrap">
    <div class="br-page-head">
      <div>
        <div class="br-title">Rapoarte probleme</div>
        <div class="br-sub">Sesizări trimise de utilizatori din aplicație</div>
      </div>
      <v-btn variant="outlined" color="#ff2c21" @click="load" :loading="loading">
        <v-icon start>mdi-refresh</v-icon>Reîmprospătează
      </v-btn>
    </div>

    <DxDataGrid :data-source="items" key-expr="ID" height="calc(100vh - 190px)"
                :show-borders="true" :show-column-lines="false" :show-row-lines="true"
                :row-alternation-enabled="true" :hover-state-enabled="true"
                @row-click="openDetail">
      <DxSearchPanel :visible="true" placeholder="Caută..." />
      <DxFilterRow :visible="true" />
      <DxPaging :page-size="20" />
      <DxColumn data-field="CreatedAt" caption="Data" data-type="datetime" format="dd.MM.yyyy HH:mm" sort-order="desc" />
      <DxColumn data-field="UserName" caption="Utilizator" />
      <DxColumn data-field="Companie" caption="Companie" />
      <DxColumn data-field="Ruta" caption="Pagina" />
      <DxColumn data-field="VersiuneApp" caption="Versiune" :width="90" />
      <DxColumn data-field="Stare" caption="Stare" :width="110" cell-template="stareTpl" />
      <DxColumn data-field="AreScreenshot" caption="Captură" data-type="boolean" :width="80" />
      <template #stareTpl="{ data }">
        <span class="br-chip" :class="'st-' + (data.value || 'Nou').toLowerCase()">{{ data.value }}</span>
      </template>
    </DxDataGrid>

    <!-- Detaliu -->
    <v-dialog v-model="detailDialog" max-width="1200px" width="95vw" scrollable>
      <v-card v-if="detail" class="br-detail-card">
        <div class="br-header">
          <v-icon color="white" size="20" class="mr-2">mdi-bug-outline</v-icon>
          <span>Raport — {{ formatDate(detail.CreatedAt) }}</span>
          <v-spacer />
          <v-btn icon variant="text" density="comfortable" @click="detailDialog = false">
            <v-icon color="white" size="20">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="br-detail-body">
          <div class="br-meta">
            <div><b>Utilizator:</b> {{ detail.UserName }}</div>
            <div><b>Companie:</b> {{ detail.Companie }}</div>
            <div><b>Pagina:</b> {{ detail.Ruta }}</div>
            <div><b>Versiune:</b> {{ detail.VersiuneApp }}</div>
            <div class="br-ua"><b>Browser:</b> {{ detail.Browser }}</div>
          </div>

          <div class="br-stare-row">
            <span class="br-label">Stare</span>
            <v-select v-model="detail.Stare" :items="stariPosibile" density="compact"
                      variant="outlined" hide-details style="max-width:220px"
                      @update:model-value="changeStare" />
          </div>

          <div class="br-label mt">Descriere</div>
          <div class="br-box">{{ detail.Descriere || '—' }}</div>

          <div class="br-label mt">Erori consolă</div>
          <pre class="br-console">{{ detail.ConsoleLog || '(fără erori captate)' }}</pre>

          <div class="br-label mt">Activitate rețea (API)</div>
          <pre class="br-console">{{ detail.NetworkLog || '(fără activitate captată)' }}</pre>

          <template v-if="screenshotUrl">
            <div class="br-label mt">Captură de ecran</div>
            <a :href="screenshotUrl" target="_blank" rel="noopener">
              <img class="br-screenshot" :src="screenshotUrl" alt="captură" />
            </a>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { DxDataGrid, DxColumn, DxPaging, DxFilterRow, DxSearchPanel } from 'devextreme-vue/data-grid';
import notify from 'devextreme/ui/notify';
import BugReportService from '@/services/BugReportService';

export default defineComponent({
  name: 'BugReports',
  components: { DxDataGrid, DxColumn, DxPaging, DxFilterRow, DxSearchPanel },
  data() {
    return {
      items: [] as any[],
      loading: false,
      detailDialog: false,
      detail: null as any,
      screenshotUrl: '' as string,
      stariPosibile: ['Nou', 'InLucru', 'Rezolvat', 'Respins'],
    };
  },
  mounted() { this.load(); },
  methods: {
    load() {
      this.loading = true;
      BugReportService.getAll()
        .then((r: any) => { this.items = r.data || []; })
        .catch(() => notify('Nu am putut încărca rapoartele.', 'error', 3000))
        .finally(() => { this.loading = false; });
    },
    openDetail(e: any) {
      if (!e?.data?.ID) return;
      this.revokeShot();
      BugReportService.getById(e.data.ID)
        .then((r: any) => {
          this.detail = r.data;
          this.detailDialog = true;
          if (r.data?.AreScreenshot) this.loadScreenshot(r.data.ID);
        })
        .catch(() => notify('Nu am putut încărca raportul.', 'error', 3000));
    },
    loadScreenshot(id: string) {
      BugReportService.getScreenshot(id)
        .then((r: any) => { this.screenshotUrl = URL.createObjectURL(r.data); })
        .catch(() => { this.screenshotUrl = ''; });
    },
    revokeShot() {
      if (this.screenshotUrl) { URL.revokeObjectURL(this.screenshotUrl); this.screenshotUrl = ''; }
    },
    changeStare(stare: string) {
      if (!this.detail?.ID) return;
      BugReportService.updateStare(this.detail.ID, stare)
        .then(() => {
          notify('Stare actualizată.', 'success', 2000);
          const row = this.items.find((x: any) => x.ID === this.detail.ID);
          if (row) row.Stare = stare;
        })
        .catch(() => notify('Nu am putut actualiza starea.', 'error', 3000));
    },
    formatDate(d: string) {
      const dt = new Date(d);
      return isNaN(dt.getTime()) ? '' : dt.toLocaleString('ro-RO');
    },
  },
});
</script>

<style scoped>
.br-wrap { padding: 18px; background: #f8f9fc; min-height: 100%; }
.br-page-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.br-title { font-size: 18px; font-weight: 700; color: #1e293b; }
.br-sub { font-size: 12px; color: #94a3b8; }
.br-chip { padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .5px; background: #f1f5f9; color: #475569; }
.st-nou { background: #eff6ff; color: #1d4ed8; }
.st-inlucru { background: #fef3c7; color: #92400e; }
.st-rezolvat { background: #ecfdf5; color: #065f46; }
.st-respins { background: #fee2e2; color: #991b1b; }
.br-header { display: flex; align-items: center; padding: 12px 18px; color: #fff;
  font-weight: 700; font-size: 15px; background: linear-gradient(90deg, #ff2c21, #ff6a4a); }
.br-detail-body { padding: 18px !important; }
.br-meta { font-size: 12px; color: #475569; line-height: 1.7; }
.br-ua { word-break: break-all; }
.br-stare-row { display: flex; align-items: center; gap: 12px; margin-top: 12px; }
.br-label { font-size: 11px; font-weight: 600; text-transform: uppercase; color: #94a3b8; letter-spacing: .5px; }
.br-label.mt { margin-top: 14px; margin-bottom: 4px; display: block; }
.br-box { background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 12px;
  font-size: 13px; color: #334155; white-space: pre-wrap; }
.br-console { background: #0f172a; color: #e2e8f0; border-radius: 8px; padding: 14px;
  font-size: 13px; line-height: 1.5; max-height: 55vh; overflow: auto; white-space: pre-wrap; word-break: break-word; }
.br-screenshot { width: 100%; border: 1px solid #e5e7eb; border-radius: 8px; cursor: zoom-in; }
</style>
